import React, { useCallback, useEffect, useState } from "react";
import { Upload, Plus, Search, Trash2, Mail, Phone, BellRing, ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { BulkImportDialog, CONTACT_KINDS } from "../../components/BulkImportDialog";
import { useSalesMe } from "../../lib/sales";
import { LeadsBoard } from "../../components/sales/LeadsBoard";
import { LeadProfile } from "../../components/sales/LeadProfile";

const LEAD_GROUP = { planner: "planners", vendor: "vendors", client: "clients" };

const STATUSES = ["lead", "booked", "completed", "lost"];
const STATUS_CLS = { lead: "bg-sky-50 text-sky-700", booked: "bg-emerald-50 text-emerald-700", completed: "bg-[#C8A96B]/15 text-[#8a6d35]", lost: "bg-black/5 text-[var(--muted)]" };

function FollowUps({ refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/contacts/follow-ups").then((r) => setRows(r.data)).catch(() => {}); }, [refreshKey]);
  async function done(id) { await api.post(`/contacts/${id}/contacted`); setRows(rows.filter((r) => r.contact_id !== id)); window.dispatchEvent(new Event("lif:cold-leads")); toast.success("Marked as contacted"); }
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="follow-ups">
      <p className="font-serif text-xl flex items-center gap-2"><BellRing className="w-5 h-5 text-amber-600" /> {rows.length} {rows.length === 1 ? "lead needs" : "leads need"} a follow-up</p>
      <p className="text-xs text-amber-800/80 mb-3">No contact in over a week. Reach out before they go cold.</p>
      <div className="space-y-2">
        {rows.slice(0, 8).map((r) => (
          <div key={r.contact_id} className="flex flex-wrap items-center gap-2 bg-white rounded-xl px-3 py-2 text-sm" data-testid={`follow-up-${r.contact_id}`}>
            <span className="flex-1 min-w-0 truncate"><b>{r.name}</b> <span className="text-[var(--muted)]">· quiet {r.days_quiet ?? "7+"} days</span></span>
            {r.email && <a href={`mailto:${r.email}`} className="text-xs text-[var(--gold)]">Email</a>}
            {r.phone && <a href={`tel:${r.phone}`} className="text-xs text-[var(--gold)]">Call</a>}
            <button onClick={() => done(r.contact_id)} className="text-xs px-3 py-1 rounded-full bg-[var(--ink)] text-white" data-testid={`follow-up-done-${r.contact_id}`}>Contacted</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DigestSettings() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/contacts/lead-digest").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  async function save(next) {
    try { setP((await api.put("/contacts/lead-digest", next)).data); toast.success("Reminder settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function sendNow() {
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/lead-digest/send-now");
      const parts = [data.email, data.sms].filter(Boolean);
      const failed = parts.find((x) => !x.ok);
      if (failed) toast.error(failed.message);
      else toast.success(parts.some((x) => x.mocked) ? "Test reminder logged (sending isn't switched on yet)" : "Reminder sent. Check your inbox and phone.");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }
  return (
    <div className="rounded-2xl border border-[var(--line)] bg-white p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="lead-digest-settings">
      <p className="text-sm flex-1"><b className="font-serif text-lg font-normal">Weekly cold-lead reminder</b><span className="block text-xs text-[var(--muted)]">Every Monday morning we'll send you the leads that have gone quiet for a week.</span></p>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.email} onChange={(e) => save({ ...p, email: e.target.checked })} className="accent-[var(--gold)]" data-testid="digest-email-toggle" /> Email</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={p.sms} onChange={(e) => (e.target.checked && !p.phone ? setP({ ...p, sms: true, dirty: true }) : save({ ...p, sms: e.target.checked }))} className="accent-[var(--gold)]" data-testid="digest-sms-toggle" /> Text</label>
      {p.sms && <form onSubmit={(e) => { e.preventDefault(); save({ email: p.email, sms: true, phone: p.phone }); }} className="flex gap-2">
        <input value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} placeholder="Mobile, e.g. +15551234567" className="bg-[var(--bg)] rounded-xl px-3 py-2 text-sm w-48 outline-none" data-testid="digest-phone-input" />
        <button className="text-xs px-3 rounded-full bg-[var(--ink)] text-white" data-testid="digest-phone-save">Save</button>
      </form>}
      {(p.email || p.sms) && <button disabled={busy} onClick={sendNow} className="text-xs px-4 py-2 rounded-full border border-[var(--gold)] text-[#8a6d35] disabled:opacity-60" data-testid="digest-send-now">{busy ? "Sending…" : "Send me one now"}</button>}
    </div>
  );
}

function PlanButton({ c, access, onRequested }) {
  const nav = useNavigate();
  const a = access[c.contact_id];
  async function go() {
    if (a?.status === "approved") return nav(`/dashboard/client-plan/${a.wedding_id}`);
    try { const { data } = await api.post("/plan-access/request", { contact_id: c.contact_id }); toast.success("Request sent. The couple will approve it in their timeline."); onRequested(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return <button onClick={go} disabled={a?.status === "pending"} data-testid={`contact-plan-${c.contact_id}`} className="text-[11px] px-3 py-1.5 rounded-full border border-[var(--gold)] text-[#8a6d35] flex items-center gap-1 disabled:opacity-60"><ClipboardList className="w-3.5 h-3.5" />{a?.status === "approved" ? "Open plan" : a?.status === "pending" ? "Awaiting approval" : "Request plan access"}</button>;
}

function ContactRow({ c, onChange, onDelete, planSlot }) {
  const [v, setV] = useState({ value: c.value || 0, paid: c.paid || 0 });
  const patch = async (d) => { try { onChange((await api.patch(`/contacts/${c.contact_id}`, d)).data); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-4 grid md:grid-cols-[1.4fr_1fr_auto_auto] gap-3 items-center" data-testid={`contact-${c.contact_id}`}>
      <div className="min-w-0">
        <p className="font-serif text-lg truncate">{c.name}</p>
        <p className="text-xs text-[var(--muted)] flex flex-wrap gap-x-3">
          {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[var(--gold)]"><Mail className="w-3 h-3" />{c.email}</a>}
          {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-[var(--gold)]"><Phone className="w-3 h-3" />{c.phone}</a>}
          {c.company && <span>{c.company}</span>}{c.role && <span>{c.role}</span>}
        </p>
      </div>
      {c.kind === "client" ? (
        <div className="flex flex-wrap items-center gap-2">
          <select value={c.status || "lead"} onChange={(e) => patch({ status: e.target.value })} data-testid={`contact-status-${c.contact_id}`} className={`text-xs rounded-full px-3 py-1.5 border-0 ${STATUS_CLS[c.status || "lead"]}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {[["value", "Value $"], ["paid", "Paid $"]].map(([k, l]) => (
            <label key={k} className="text-[11px] text-[var(--muted)] flex items-center gap-1">{l}
              <input type="number" min={0} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} onBlur={() => Number(v[k]) !== Number(c[k] || 0) && patch({ [k]: Number(v[k]) || 0 })} data-testid={`contact-${k}-${c.contact_id}`} className="w-20 bg-[var(--bg)] rounded-lg px-2 py-1 text-[var(--ink)]" />
            </label>
          ))}
        </div>
      ) : <p className="text-xs text-[var(--muted)] truncate">{c.notes}</p>}
      {planSlot}
      <button onClick={() => window.confirm(`Remove ${c.name}?`) && onDelete(c.contact_id)} data-testid={`contact-delete-${c.contact_id}`} className="justify-self-end p-2 rounded-full text-[var(--muted)] hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function AddContact({ kind, onAdded }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  async function add(e) {
    e.preventDefault();
    try { onAdded((await api.post("/contacts", { ...f, kind })).data); setF({ name: "", email: "", phone: "" }); }
    catch (err) { toast.error(formatApiError(err)); }
  }
  return (
    <form onSubmit={add} className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 bg-white border border-[var(--line)] rounded-2xl p-3" data-testid="add-contact-form">
      {["name", "email", "phone"].map((k) => <input key={k} required={k === "name"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={k[0].toUpperCase() + k.slice(1)} data-testid={`add-contact-${k}`} className="bg-[var(--bg)] rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-[var(--gold)] min-w-0" />)}
      <button className="btn-gold !py-2.5" data-testid="add-contact-submit"><Plus className="w-4 h-4" /> Add</button>
    </form>
  );
}

export default function ContactsPage() {
  const { user } = useAuth();
  const pro = ["vendor", "planner"].includes(user?.role);
  const [kind, setKind] = useState(pro ? "client" : "guest");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [importing, setImporting] = useState(false);
  const [hasWedding, setHasWedding] = useState(false);
  const [access, setAccess] = useState({});
  const salesMe = useSalesMe();
  const [openPid, setOpenPid] = useState("");
  const [leadTick, setLeadTick] = useState(0);
  const isPlanner = user?.role === "planner" || user?.role === "admin";
  useEffect(() => { if (isPlanner) api.get("/plan-access/mine").then((r) => setAccess(Object.fromEntries(r.data.map((a) => [a.contact_id, a])))).catch(() => {}); }, [isPlanner]);

  const load = useCallback(() => api.get(`/contacts?kind=${kind}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then((r) => setRows(r.data)).catch(() => setRows([])), [kind, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/weddings/mine").then((r) => setHasWedding(!!r.data)).catch(() => {}); }, []);

  async function del(id) { await api.delete(`/contacts/${id}`); setRows(rows.filter((r) => r.contact_id !== id)); }

  return (
    <div className="max-w-5xl min-w-0 w-full space-y-5" data-testid="contacts-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">{pro ? "Client book" : "Your people"}</p>
          <h1 className="font-serif font-light text-4xl">Contacts</h1>
          <p className="text-sm text-[var(--muted)] mt-1">Keep guests, clients, planners and vendors organized in one place. Upload a whole list at once.</p>
        </div>
        <button onClick={() => setImporting(true)} className="btn-gold" data-testid="open-bulk-import"><Upload className="w-4 h-4" /> Bulk upload</button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex gap-1 overflow-x-auto no-scrollbar min-w-0">
          {CONTACT_KINDS.map((k) => <button key={k.v} onClick={() => setKind(k.v)} data-testid={`contacts-tab-${k.v}`} className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] whitespace-nowrap ${kind === k.v ? "bg-[var(--ink)] text-white" : "text-[var(--muted)] hover:text-[var(--ink)]"}`}>{k.l}</button>)}
        </div>
        <label className="flex items-center gap-2 bg-white border border-[var(--line)] rounded-full px-4"><Search className="w-4 h-4 text-[var(--muted)]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" data-testid="contacts-search" className="py-2.5 text-sm outline-none bg-transparent w-full min-w-0 sm:w-40" /></label>
      </div>
      {kind === "client" && <FollowUps refreshKey={rows?.length} />}
      {salesMe && LEAD_GROUP[kind] && <LeadsBoard title={`Call Center ${kind === "client" ? "couples" : `${kind}s`}`} fixedKind={LEAD_GROUP[kind]} onOpen={setOpenPid} refresh={leadTick} />}
      {salesMe && <LeadProfile pid={openPid} me={salesMe} onClose={() => setOpenPid("")} onChanged={() => setLeadTick((n) => n + 1)} />}
      {kind === "client" && pro && <DigestSettings />}
      <AddContact kind={kind} onAdded={(c) => setRows([c, ...(rows || [])])} />
      <div className="space-y-2">
        {rows === null && <div className="h-20 rounded-2xl bg-white/70 animate-pulse" />}
        {rows?.length === 0 && <p className="text-center text-sm text-[var(--muted)] py-12" data-testid="contacts-empty">No {kind}s yet. Add one above or bulk upload a list.</p>}
        {rows?.map((c) => <ContactRow key={c.contact_id} c={c} onDelete={del} planSlot={isPlanner && c.kind === "client" ? <PlanButton c={c} access={access} onRequested={(a) => setAccess({ ...access, [c.contact_id]: a })} /> : null} onChange={(n) => setRows(rows.map((r) => (r.contact_id === n.contact_id ? n : r)))} />)}
      </div>
      <BulkImportDialog open={importing} onClose={() => setImporting(false)} defaultKind={kind} hasWedding={hasWedding} onSaved={(d) => (d.kind !== kind ? setKind(d.kind) : load())} />
    </div>
  );
}
